import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { CustomTranslationService } from '../../translation/custom-translation.service';
import { PostAddressService } from 'src/app/api/services';
import { componentDestroyed, untilComponentDestroyed } from '../../helpers/rxjs.operator';
import { Address, AddressCity, AddressStreet, Customer, CustomerDetail, IsoCountry } from 'src/app/api/models';
import { Languages } from '../../translation/languages.enum';
import { debounceTime, distinctUntilChanged, filter, finalize, Observable, of, startWith, switchMap, take, takeUntil } from 'rxjs';
import { MatSelectChange } from '@angular/material/select';
import { DEBOUNCE_TIME_INPUT, TELEPHONE_REGEX } from '../../helpers/constants';
import { Store, select } from '@ngrx/store';
import * as fromStaticDataSelectors from 'src/app/store/selectors/static-data.selector';
import * as fromStaticDataReasonActions from 'src/app/store/actions/static-data.action';
import { AppState } from 'src/app/store/states/app.state';
import { AddressFormConfiguration } from 'src/app/add-customer/models/address-form-configuration';
import { NewAddressForm } from './models/new-address-form.model';
import { firstNameLastNameFirmNameValidator } from './custom-validators/firstname-lastname-firmname.validator';
interface StreetHouseNumber {
  name: string;
  houseNumber: string;
}
interface letterSalutation {
  group: string,
  name: string
}
// Post box translation are to used with the languageCode from the postCity object
enum PostBoxTranslations {
  de = 'Postfach',
  fr = 'Case postale',
  it = 'Casealla postale',
  rr = 'Casealla postale'
}
@Component({
  selector: 'diana-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
  standalone: false
})
export class AddressFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() title = 'registerAddress.title';
  @Input() readOnly = false;
  @Input() config = new AddressFormConfiguration();
  @Input() idSuffix = '';
  @Input() customer?: CustomerDetail;
  @Input() oldAddress: Address | undefined = undefined;
  @Input() disableForm: boolean | undefined = false;
  @Output() newChanges = new EventEmitter<NewAddressForm | undefined | null>();
  @Output() isCityAddressValid = new EventEmitter<boolean>();

  public formGroup!: FormGroup;
  public addressSalutionList: Array<string> = [
    'registerAddress.autocomplete.addressSalutation.option.family',
    'registerAddress.autocomplete.addressSalutation.option.company',
    'registerAddress.autocomplete.addressSalutation.option.mrs',
    'registerAddress.autocomplete.addressSalutation.option.mr'
  ]
  public letterSalutionAvailable: Array<letterSalutation> = [
    {
      group: 'registerAddress.autocomplete.addressSalutation.option.family',
      name: 'registerAddress.autocomplete.letterSalutation.family.option.dearFamily'
    },
    {
      group: 'registerAddress.autocomplete.addressSalutation.option.family',
      name: 'registerAddress.autocomplete.letterSalutation.family.option.dearFamilyDear'
    },
    {
      group: 'registerAddress.autocomplete.addressSalutation.option.company',
      name: 'registerAddress.autocomplete.letterSalutation.company.option.dearLadiesAndGentlemen'
    },
    {
      group: 'registerAddress.autocomplete.addressSalutation.option.mrs',
      name: 'registerAddress.autocomplete.letterSalutation.mrs.option.dearMrs'
    },
    {
      group: 'registerAddress.autocomplete.addressSalutation.option.mrs',
      name: 'registerAddress.autocomplete.letterSalutation.mrs.option.dearMrsDr'
    },
    {
      group: 'registerAddress.autocomplete.addressSalutation.option.mrs',
      name: 'registerAddress.autocomplete.letterSalutation.mrs.option.dear'
    },
    {
      group: 'registerAddress.autocomplete.addressSalutation.option.mr',
      name: 'registerAddress.autocomplete.letterSalutation.mr.option.dearMr'
    },
    {
      group: 'registerAddress.autocomplete.addressSalutation.option.mr',
      name: 'registerAddress.autocomplete.letterSalutation.mr.option.dearMrDr'
    },
    {
      group: 'registerAddress.autocomplete.addressSalutation.option.mr',
      name: 'registerAddress.autocomplete.letterSalutation.mr.option.dear'
    }
  ];
  private selectedLetterSalutation: letterSalutation | undefined = undefined;
  get showSalutationsTitleControl(): FormControl {
    return this.formGroup.get('showSalutationsTitle') as FormControl;
  }
  get showStatesCantonControl(): FormControl {
    return this.formGroup.get('showStatesCanton') as FormControl;
  }
  get showPhonesControl(): FormControl {
    return this.formGroup.get('showPhones') as FormControl;
  }
  get showWebControl(): FormControl {
    return this.formGroup.get('showWeb') as FormControl;
  }
  get countryControl(): FormControl {
    return this.formGroup.get('country1') as FormControl;
  }
  get postCodeControl(): FormControl {
    return this.formGroup.get('postCode') as FormControl;
  }
  get cityControl(): FormControl {
    return this.formGroup.get('city') as FormControl;
  }
  get countyControl(): FormControl {
    return this.formGroup.get('county') as FormControl;
  }
  get streetControl(): FormControl {
    return this.formGroup.get('street') as FormControl;
  }
  get stateControl(): FormControl {
    return this.formGroup.get('country2') as FormControl;
  }
  get languageControl(): FormControl {
    return this.formGroup.get('language') as FormControl;
  }
  get addressSalutationControl(): FormControl {
    return this.formGroup.get('addressSalutation') as FormControl;
  }
  get letterSalutationControl(): FormControl {
    return this.formGroup.get('letterSalutation') as FormControl;
  }
  get firstNameControl(): FormControl {
    return this.formGroup.get('firstName') as FormControl;
  }
  get lastNameControl(): FormControl {
    return this.formGroup.get('lastName') as FormControl;
  }
  get firmNameControl(): FormControl {
    return this.formGroup.get('firmName') as FormControl;
  }
  get titleControl(): FormControl {
    return this.formGroup.get('title') as FormControl;
  }
  get webControl(): FormControl {
    return this.formGroup.get('web') as FormControl;
  }
  get eMailControl(): FormControl {
    return this.formGroup.get('eMail') as FormControl;
  }
  get privatePhoneControl(): FormControl {
    return this.formGroup.get('privatePhone') as FormControl;
  }
  get cellPhoneControl(): FormControl {
    return this.formGroup.get('cellPhone') as FormControl;
  }
  get cellPhone2Control(): FormControl {
    return this.formGroup.get('cellPhone2') as FormControl;
  }
  get businessPhoneControl(): FormControl {
    return this.formGroup.get('businessPhone') as FormControl;
  }
  get houseNumberControl(): FormControl {
    return this.formGroup.get('houseNumber') as FormControl;
  }
  public letterSalutionList: Array<letterSalutation> = [];
  public countryList: Array<IsoCountry> = [];
  public countyList: Array<string> = [];
  public languageList: Array<string> = [
    Languages.de.toUpperCase(),
    Languages.fr.toUpperCase(),
    Languages.it.toUpperCase(),
  ];
  public filteredPostCode: AddressCity[] = [];
  public selectedAddressCity: AddressCity | undefined = undefined;
  public filteredCity: AddressCity[] = [];
  public showLoadingPostCity = false;
  public postStreetList: Array<AddressStreet> = [];
  public selectedPosStreet: AddressStreet | string | undefined = undefined;
  public currentLanguage: Languages = Languages.de;
  constructor(
    private fb: FormBuilder,
    private store$: Store<AppState>,
    private translateSrv: CustomTranslationService,
    private postAddressService: PostAddressService,
  ) {
    this.store$.dispatch(fromStaticDataReasonActions.initCountryList());
    this.store$.dispatch(fromStaticDataReasonActions.initCountyList());
  }

  ngOnInit(): void {

    this.currentLanguage = (this.translateSrv.currentLang as Languages);
    if (this.customer && !this.customer.isActive) {
      this.config.disableForm();
    }
    this.formGroup = this.fb.group(
      {
        firstName: [{ value: this.customer ? this.customer.firstName : '', disabled: this.config.firstName.isDisable }, this.config.firstName.isRequired ? Validators.required : [],],
        lastName: [{ value: this.customer ? this.customer.lastName : '', disabled: this.config.lastName.isDisable }, this.config.lastName.isRequired ? Validators.required : []],
        firmName: [{ value: '', disabled: this.config.firmName.isDisable }, this.config.firmName.isRequired ? Validators.required : []],
        addressSalutation: [{ value: '', disabled: this.config.addressSalutation.isDisable }, this.config.addressSalutation.isRequired ? Validators.required : []],
        title: [{ value: '', disabled: this.config.title.isDisable }, this.config.title.isRequired ? Validators.required : []],
        letterSalutation: [{ value: '', disabled: this.config.letterSalutation.isDisable }, this.config.letterSalutation.isRequired ? Validators.required : []],
        country1: [{ value: '', disabled: this.config.country.isDisable }, this.config.country.isRequired ? Validators.required : []],
        language: [{ value: this.customer ? this.customer.language : this.currentLanguage.toUpperCase(), disabled: this.config.language.isDisable }, this.config.language.isRequired ? Validators.required : []],
        postCode: [{ value: '', disabled: this.config.postCode.isDisable }, this.config.postCode.isRequired ? Validators.required : []],
        city: [{ value: '', disabled: this.config.city.isDisable }, this.config.city.isRequired ? Validators.required : []],
        street: [{ value: '', disabled: this.config.street.isDisable }, this.config.street.isRequired ? Validators.required : []],
        houseNumber: [{ value: this.customer ? this.splitStreetHouseNumber(this.customer.location?.street).houseNumber : '', disabled: this.config.firstName.isDisable }, this.config.houseNumber.isRequired ? Validators.required : []],
        country2: [{ value: '', disabled: this.config.state.isDisable }, this.config.state.isRequired ? Validators.required : []],
        county: [{ value: '', disabled: this.config.county.isDisable }, this.config.county.isRequired ? Validators.required : []],
        privatePhone: [{ value: this.customer ? this.customer.phone1 : '', disabled: this.config.privatePhone.isDisable }, this.config.privatePhone.isRequired ? [Validators.required] : []],
        cellPhone: [{ value: this.customer ? this.customer.mobile1 : '', disabled: this.config.cellPhone.isDisable }, this.config.cellPhone.isRequired ? [Validators.required] : []],
        cellPhone2: [{ value: this.customer ? this.customer.mobile2 : '', disabled: this.config.cellPhone2.isDisable }, this.config.cellPhone2.isRequired ? [Validators.required] : []],
        businessPhone: [{ value: this.customer ? this.customer.phone2 : '', disabled: this.config.businessPhone.isDisable }, this.config.businessPhone.isRequired ? [Validators.required] : []],
        eMail: [{ value: this.customer ? this.customer.email : '', disabled: this.config.eMail.isDisable }, this.config.eMail.isRequired ? [Validators.required, Validators.email] : [Validators.email]],
        web: [{ value: '', disabled: this.config.web.isDisable }, this.config.web.isRequired ? Validators.required : []],
        showSalutationsTitle: [(this.config.addressSalutation.isVisible || this.config.title.isVisible || this.config.letterSalutation.isVisible) === false ? false : this.config.isSalutationTitleEnabled],
        showStatesCanton: [(this.config.county.isVisible || this.config.state.isVisible) === false ? false : this.config.isSalutationTitleEnabled],
        showPhones: [(this.config.privatePhone.isVisible || this.config.cellPhone.isVisible || this.config.businessPhone.isVisible) === false ? false : this.config.isPhoneEnabled],
        showWeb: [(this.config.eMail.isVisible || this.config.web.isVisible) === false ? false : this.config.isWebEnabled],
      }
    );
    // Init form with customer data
    if (this.customer) {
      const addressSalutationTranslation = this.getAddressSalutationTranslation(this.customer?.salutation || '');
      this.letterSalutionList = this.letterSalutionAvailable.filter(letterSalution => letterSalution.group === addressSalutationTranslation);
      this.addressSalutationControl.patchValue(addressSalutationTranslation, { emitEvent: false });
      this.letterSalutationControl.patchValue(this.customer?.formalSalutation, { emitEvent: false });
      this.updateCityAddress(this.customer.location?.postalCode || '', this.customer.location?.city || '', this.splitStreetHouseNumber(this.customer.location?.street).name || '', undefined, undefined);
      this.formGroup.markAsPristine();
    }
    // Adding validator for at least one of this fields are fill
    if (this.config.firstName.isVisible && this.config.lastName.isVisible && this.config.firmName.isVisible) {
      this.formGroup.setValidators(firstNameLastNameFirmNameValidator(['firstName', 'lastName', 'firmName']));
      this.formGroup.updateValueAndValidity()
    }
    this.firstLastNameChangeHandler();

    // Clean letter salutation list if address salution invalid
    this.addressSalutationControl.valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe(data => {
        const existLetterSalution = this.letterSalutionAvailable.find(letterSalution => letterSalution.group === data);
        if (!existLetterSalution) {
          this.letterSalutionList = [];
        }
      });
    this.formGroup.valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        const customerAddress = this.formGroup.getRawValue();
        delete customerAddress.showPhones;
        delete customerAddress.showSalutationsTitle;
        delete customerAddress.showStatesCanton;
        delete customerAddress.showWeb;
        const customerAddressResult: NewAddressForm = customerAddress;
        customerAddressResult.isValid = this.formGroup.valid;
        customerAddressResult.isTouched = this.formGroup.touched;
        customerAddressResult.isDirty = this.formGroup.dirty;
        customerAddressResult.letterSalutation = this.letterSalutationControl.value?.name ? this.setLetterSalutationText(this.letterSalutationControl.value) : this.letterSalutationControl.value;
        customerAddressResult.addressSalutation = customerAddressResult.addressSalutation ? this.translateSrv.instant(customerAddressResult.addressSalutation) : '';
        this.isCityAddressValid.emit(this.checkValidAddress());
        this.newChanges.emit(customerAddressResult);

      });
    // to prevent angular error ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.getCountryList();
    });
  }

  private splitStreetHouseNumber(streetNumber: string | undefined): StreetHouseNumber {
    const split = {
      name: '',
      houseNumber: ''
    }
    if (streetNumber) {
      const lastSpaceIndex = streetNumber.lastIndexOf(' ');

      if (lastSpaceIndex === -1) {
        split.name = streetNumber;
        split.houseNumber = '';
      } else {
        split.name = streetNumber.substring(0, lastSpaceIndex);
        split.houseNumber = streetNumber.substring(lastSpaceIndex + 1);
      }
    }
    return split;
  }

  private updateCityAddress(postCode: string, city: string, street: string, postCityId: number | undefined, postStreetId: number | undefined): void {
    if (postCityId || city) {
      this.filterCityAddress(postCode, city).pipe(untilComponentDestroyed(this), take(1))
        .subscribe((data) => {
          let postCityElement = undefined;
          if (postCityId) {
            postCityElement = data.find(postCity => postCity.id === postCityId);
          } else if (city) {
            postCityElement = data.find(postCity => postCity.name === city);
          }
          if (postCityElement) {
            this.selectedAddressCity = postCityElement;
            this.postCodeControl.patchValue(postCityElement);
            this.cityControl.patchValue(postCityElement);
            if (postStreetId || street) {
              this.filterPostStreet(postCityElement.classification, '').pipe(untilComponentDestroyed(this))
                .subscribe((streets) => {
                  let streetElement = undefined;
                  if (postStreetId) {
                    streetElement = streets.find(streetData => streetData.id === postStreetId);
                  } else if (street) {
                    streetElement = streets.find(streetData => streetData.name === street);
                  }
                  if (streetElement) {
                    this.streetControl.patchValue(streetElement);
                    this.selectedPosStreet = streetElement;
                  } else {
                    this.streetControl.patchValue(street);
                    // to be able to set the selectedPosStreet as a PostBoxTranslations and show the green color on the street input
                    if (this.isPoBox(street as PostBoxTranslations)) {
                      this.selectedPosStreet = street;
                    }
                  }
                })
            } else {
              this.streetControl.patchValue(street, { emitEvent: false });
            }
          } else {
            this.postCodeControl.patchValue(postCode, { emitEvent: false });
            this.cityControl.patchValue(city, { emitEvent: false });
            this.streetControl.patchValue(street, { emitEvent: false });
          }
        });
    } else {
      this.postCodeControl.patchValue(postCode, { emitEvent: false });
      this.cityControl.patchValue(city, { emitEvent: false });
      this.streetControl.patchValue(street, { emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    componentDestroyed(this);
  }
  private checkValidAddress(): boolean {
    if (this.countryControl?.value?.abbreviation !== 'CH') {
      return true;
    }
    return this.selectedAddressCity?.id !== undefined && (this.streetControl.value?.id !== undefined || this.isPoBox(this.streetControl.value as PostBoxTranslations))
  }

  private initiateForm(): void {
    if (this.oldAddress) {
      const country = this.oldAddress?.country1Id ? this.countryList.find(country => country.id === this.oldAddress?.country1Id) : '';
      const state = this.oldAddress?.country2Id ? this.countryList.find(country => country.id === this.oldAddress?.country2Id) : '';

      let countyAux: IsoCountry | string | undefined = '';
      if (country && country?.abbreviation !== 'CH') {
        countyAux = this.oldAddress?.county || '';
        this.countyControl.clearValidators();
      }
      else {
        countyAux = this.oldAddress?.county ? this.countyList.find(county => county === this.oldAddress?.county) : '';
      }

      const addressSalutationTranslation = this.getAddressSalutationTranslation(this.oldAddress?.addressSalutation || '');
      this.letterSalutionList = this.letterSalutionAvailable.filter(letterSalution => letterSalution.group === addressSalutationTranslation);
      this.firstNameControl.patchValue(this.oldAddress?.firstName, { emitEvent: false });
      this.firmNameControl.patchValue(this.oldAddress?.firmName, { emitEvent: false });
      this.lastNameControl.patchValue(this.oldAddress?.lastName, { emitEvent: false });
      this.addressSalutationControl.patchValue(addressSalutationTranslation, { emitEvent: false });
      this.letterSalutationControl.patchValue(this.oldAddress?.letterSalutation, { emitEvent: false });
      this.languageControl.patchValue(this.oldAddress?.language ? this.oldAddress?.language : this.currentLanguage.toUpperCase(), { emitEvent: false });
      this.countryControl.patchValue(country, { emitEvent: false });
      this.titleControl.patchValue(this.oldAddress?.title, { emitEvent: false });
      this.houseNumberControl.patchValue(this.oldAddress?.houseNumber, { emitEvent: false });
      this.stateControl.patchValue(state, { emitEvent: false });
      this.countyControl.patchValue(countyAux, { emitEvent: false });
      this.privatePhoneControl.patchValue(this.oldAddress?.privatePhone, { emitEvent: false });
      this.cellPhoneControl.patchValue(this.oldAddress?.cellPhone, { emitEvent: false });

      this.businessPhoneControl.patchValue(this.oldAddress?.businessPhone, { emitEvent: false });
      this.eMailControl.patchValue(this.oldAddress?.email, { emitEvent: false });
      this.webControl.patchValue(this.oldAddress?.web, { emitEvent: false });
      if (this.oldAddress?.postCityId) {
        this.updateCityAddress(this.oldAddress.postcode, this.oldAddress.city, this.oldAddress.street, this.oldAddress.postCityId, this.oldAddress.postStreetId)
      } else {
        this.postCodeControl.patchValue(this.oldAddress?.postcode, { emitEvent: false });
        this.cityControl.patchValue(this.oldAddress?.city, { emitEvent: false });
        this.streetControl.patchValue(this.oldAddress?.street, { emitEvent: false });
      }
      this.formGroup.updateValueAndValidity();
      this.formGroup.markAsPristine();
    }
  }

  private getAddressSalutationTranslation(salutation: string): string {
    let salutationTranslation = salutation
    this.addressSalutionList.map(availableSalutation => {
      if (this.translateSrv.instant(availableSalutation) === salutation) {
        salutationTranslation = availableSalutation;
      }
    });
    return salutationTranslation;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.formGroup) {
      if (this.readOnly) {
        this.formGroup.disable();
      } else {
        // the emitEvent: false is to prevent the formGroup from becaming dirty
        this.formGroup.enable({emitEvent: false});
      }
    }
    if (changes.disableForm?.isFirstChange()) {
      return;
    }

    if (changes.disableForm?.currentValue) {
      this.disableAllInputs();
    }
  }

  private disableAllInputs(): void {
    this.countryControl.disable();
    this.postCodeControl.disable();
    this.cityControl.disable();
    this.countyControl.disable();
    this.streetControl.disable();
    this.stateControl.disable();
    this.languageControl.disable();
    this.addressSalutationControl.disable();
    this.letterSalutationControl.disable();
    this.firstNameControl.disable();
    this.lastNameControl.disable();
    this.firmNameControl.disable();
    this.titleControl.disable();
    this.webControl.disable();
    this.eMailControl.disable();
    this.privatePhoneControl.disable();
    this.cellPhoneControl.disable();
    this.cellPhone2Control.disable();
    this.businessPhoneControl.disable();
    this.houseNumberControl.disable();
  }

  private firstLastNameChangeHandler(): void {
    this.firstNameControl.valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.updateSalutationOnNameChange();
      })
    this.lastNameControl.valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.updateSalutationOnNameChange();
      })
  }

  private updateSalutationOnNameChange(): void {
    if (this.selectedLetterSalutation) {
      const newLeSalutation = this.setLetterSalutationText(this.selectedLetterSalutation);
      this.letterSalutationControl.setValue(newLeSalutation);
    }
  }

  private setCityStreetSearch(): void {
    this.postCodeControl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        startWith(''),
        debounceTime(DEBOUNCE_TIME_INPUT),
        distinctUntilChanged(), // Avoid duplicate calls for the same value
        filter(value => typeof value === 'string' && this.countryControl.value && this.countryControl.value.abbreviation && this.countryControl.value.abbreviation === 'CH'), // Prevents valueChanges action when selecting a element of the list
        switchMap(data => {
          if (this.formGroup.dirty) {
            this.selectedAddressCity = undefined;
            this.countyControl.reset();
            this.resetStreet();
            if (data) {
              return this.filterCityAddress(data, this.cityControl.value.name || this.cityControl.value || ''); // Returns the observable
            }
          }
          return of(this.filteredPostCode);
        })
      ).subscribe(data => {
        if (data) {
          this.filteredPostCode = data
        }
      });
    this.cityControl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        startWith(''),
        debounceTime(DEBOUNCE_TIME_INPUT),
        distinctUntilChanged(), // Avoid duplicate calls for the same value
        filter(value => typeof value === 'string' && this.countryControl.value && this.countryControl.value.abbreviation && this.countryControl.value.abbreviation === 'CH'), // Prevents valueChanges action when selecting a element of the list
        switchMap(data => {
          if (this.formGroup.dirty) {
            this.selectedAddressCity = undefined;
            this.countyControl.reset();
            this.resetStreet();
            if (data) {
              return this.filterCityAddress(this.postCodeControl.value.name || this.postCodeControl.value || '', data); // Returns the observable
            }
          }
          return of(this.filteredCity);
        })
      ).subscribe(data => {
        if (data) {
          this.filteredCity = data
        }
      });
    this.streetControl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        startWith(''),
        debounceTime(DEBOUNCE_TIME_INPUT),
        distinctUntilChanged(), // Avoid duplicate calls for the same value
        filter(value => typeof value === 'string' && this.countryControl.value && this.countryControl.value.abbreviation && this.countryControl.value.abbreviation === 'CH'), // Prevents valueChanges action when selecting a element of the list
        switchMap(data => {
          const isPostBox = this.isPoBox(data);
          if (this.formGroup.dirty && !isPostBox) {
            this.selectedPosStreet = undefined;
            if (data && this.selectedAddressCity) {
              return this.filterPostStreet(this.selectedAddressCity.classification, data); // Returns the observable
            }
          } else if (isPostBox) {
            this.selectedPosStreet = data;
          }
          return of(this.postStreetList); // Return empty array if no data
        })
      ).subscribe(data => {
        if (data) {
          this.postStreetList = data;
        }
      });
  }

  private getCountryList(): void {
    this.store$
      .pipe(
        select(fromStaticDataSelectors.getCountryList()),
        untilComponentDestroyed(this),
      )
      .subscribe((countryList: IsoCountry[]) => {
        this.countryList = [];
        countryList.forEach(country => {
          const translateRes = this.translateCountryName(country);
          const auxCountry = { ...country };
          if (translateRes) {
            auxCountry.name = translateRes;
          }
          this.countryList.push(auxCountry);
        });
        this.countryList.sort((a, b) => a.name.localeCompare(b.name));

        const switzerland = this.countryList.find(country => country.abbreviation === 'CH');
        if (switzerland) {
          this.countryControl.patchValue(switzerland);
          this.stateControl.patchValue(switzerland);
          if (this.customer) {
            const country = this.countryList.find(country => country.abbreviation === this.customer?.location?.country);
            if (country) {
              this.countryControl.patchValue(country, { emitEvent: false });
              this.stateControl.patchValue(country, { emitEvent: false });
            }
          }
        }
        this.getCountyList();

      });
  }
  private getCountyList(): void {
    this.store$
      .pipe(
        select(fromStaticDataSelectors.getCountyList()),
        take(1),
        untilComponentDestroyed(this)
      )
      .subscribe((countyList: Array<string>) => {
        this.countyList = countyList;

        this.initiateForm();
        this.setCityStreetSearch();
      });
  }

  public handleCountryChange(e: MatSelectChange): void {
    if (e.value.abbreviation !== 'CH') {
      this.selectedAddressCity = undefined;
      this.selectedPosStreet = undefined;
      this.streetControl.reset('');
      this.postCodeControl.reset('');
      this.cityControl.reset('');
      this.countyControl.reset('');

      this.countyControl.clearValidators();
      this.countyControl.updateValueAndValidity();

      this.filteredPostCode = [];
      this.postStreetList = [];
      this.filteredCity = [];
    }
    else {
      this.countyControl.reset('');
      this.countyControl.setValidators(Validators.required);
      this.countyControl.updateValueAndValidity();
    }
    // updating state based on the selected country
    const state = this.countryList.find(state => state.abbreviation === e.value.abbreviation);
    if (state) {
      this.stateControl.setValue(state);
    }
  }

  private resetStreet(): void {
    this.selectedPosStreet = undefined;
    this.streetControl.reset(''),
      this.postStreetList = [];
  }

  public onSelectedPostCode(event: MatAutocompleteSelectedEvent): void {
    this.filteredCity = [];
    this.cityControl.patchValue(event.option.value, { emitEvent: false });
    this.setCounty(event.option.value);
    this.setPostStreetName(event.option.value);
    this.selectedAddressCity = event.option.value;
    this.isCityAddressValid.emit(this.checkValidAddress());
  }
  public onSelectedCity(event: MatAutocompleteSelectedEvent): void {
    this.filteredPostCode = [];
    this.postCodeControl.patchValue(event.option.value, { emitEvent: false });
    this.setCounty(event.option.value);
    this.setPostStreetName(event.option.value);
    this.selectedAddressCity = event.option.value;
    this.isCityAddressValid.emit(this.checkValidAddress());
  }

  private isPoBox(postBox: PostBoxTranslations): boolean {
    let valid = false;
    switch (postBox) {
      case PostBoxTranslations.de: {
        valid = true;
        break;
      }
      case PostBoxTranslations.fr: {
        valid = true;
        break;
      }
      case PostBoxTranslations.it: {
        valid = true;
        break;
      }
      case PostBoxTranslations.rr: {
        valid = true;
        break;
      }

    }
    return valid;
  }

  private setPostStreetName(city: AddressCity): void {
    this.selectedPosStreet = undefined;
    this.postStreetList = [];
    if (city.postCodeType === 'PO_BOX') {
      switch (city.languageCode) {
        case 1: {
          this.streetControl.setValue(PostBoxTranslations.de);
          break;
        }
        case 2: {
          this.streetControl.setValue(PostBoxTranslations.fr);
          break;
        }
        case 3: {
          this.streetControl.setValue(PostBoxTranslations.it);
          break;
        }
        case 4: {
          this.streetControl.setValue(PostBoxTranslations.rr);
          break;
        }

      }
      this.streetControl.markAsDirty();
      this.selectedPosStreet = this.streetControl.value;
    } else {
      this.streetControl.setValue('', { emitEvent: false });
      this.setPostStreetList(city.classification, '');
    }
  }
  public onSelectedPostStreet(event: MatAutocompleteSelectedEvent): void {
    this.selectedPosStreet = event.option.value;
    this.isCityAddressValid.emit(this.checkValidAddress());
  }

  filterCityAddress(postCode: string, town: string): Observable<Array<AddressCity>> {
    this.showLoadingPostCity = true;
    if (postCode || town) {
      return this.postAddressService.findCityAddress({
        post_code: postCode ? postCode : undefined,
        town: town ? town : undefined
      }).pipe(untilComponentDestroyed(this), take(1), finalize(() => {
        this.showLoadingPostCity = false
      }))
    } else {
      this.showLoadingPostCity = false
    }
    return of()
  }

  filterPostStreet(classification: number | null, street: string): Observable<Array<AddressStreet>> {
    this.showLoadingPostCity = true
    if (classification) {
      return this.postAddressService.findStreetAddress({
        classification: classification,
        street: street
      }).pipe(untilComponentDestroyed(this), take(1), finalize(() => {
        this.showLoadingPostCity = false
      }))
    } else {
      this.showLoadingPostCity = false
    }
    return of()
  }

  private setPostStreetList(classification: number | null, street: string): void {
    this.filterPostStreet(classification, street).pipe(untilComponentDestroyed(this)).subscribe(data => {
      this.postStreetList = data;
    })
  }

  private setCounty(address: AddressCity): void {
    const county = this.countyList.find(county => county === address.county);
    this.countyControl.setValue(county);
  }

  public translateCountryName(country: IsoCountry): string {
    if (country) {
      const translationKey = 'registerAddress.country.' + country.abbreviation;
      const translatedName = this.translateSrv.instant(translationKey);

      if (translatedName === translationKey) {
        return country.name;
      } else {
        return translatedName;
      }
    }
    return '';
  }

  public setLetterSalutation(event: MatAutocompleteSelectedEvent): void {
    this.letterSalutionList = this.letterSalutionAvailable.filter(letterSalution => letterSalution.group === event.option.value);
  }

  public displayAddressSalutation = (option: string): string => {
    if (this.translateSrv && option) {
      return this.translateSrv.instant(option);
    }
    return '';
  }
  public displayLetterSalutation = (option: letterSalutation): string => {
    return this.setLetterSalutationText(option)
  }
  public displayPostCode = (option: AddressCity): string => {
    if (option.postCode) {
      return option.postCode
    }
    return option as any
  }
  public displayGenerique = (option: AddressCity): string => {
    if (option && option.name) {
      return option.name
    }
    return option as any
  }

  public setLetterSalutationText(option: letterSalutation): string {
    if (option && option.name) {
      switch (option.name) {
        case 'registerAddress.autocomplete.letterSalutation.mrs.option.dear': {
          return `${this.translateSrv.instant('registerAddress.autocomplete.letterSalutation.mrs.option.dear')} ${this.firstNameControl.value || ''}`
        }
        case 'registerAddress.autocomplete.letterSalutation.mr.option.dear': {
          return `${this.translateSrv.instant('registerAddress.autocomplete.letterSalutation.mr.option.dear')} ${this.firstNameControl.value || ''}`
        }
        case 'registerAddress.autocomplete.letterSalutation.company.option.dearLadiesAndGentlemen': {
          return `${this.translateSrv.instant('registerAddress.autocomplete.letterSalutation.company.option.dearLadiesAndGentlemen')}`
        }
        default: {
          return `${this.translateSrv.instant(option.name)} ${this.lastNameControl.value || ''}`
        }
      }
    }
    return option as any;
  }

  public getSelectedSalutation(event: MatAutocompleteSelectedEvent): void {
    this.selectedLetterSalutation = undefined;
    if (event.option.value) {
      this.selectedLetterSalutation = event.option.value;
    }
  }

  public getCurrentAddressForm(): NewAddressForm | undefined {
    if (!this.formGroup) return undefined;

    const customerAddress = this.formGroup.getRawValue();
    delete customerAddress.showPhones;
    delete customerAddress.showSalutationsTitle;
    delete customerAddress.showStatesCanton;
    delete customerAddress.showWeb;

    const result: NewAddressForm = customerAddress;
    result.isValid = this.formGroup.valid;
    result.isTouched = this.formGroup.touched;
    result.isDirty = this.formGroup.dirty;
    result.letterSalutation = this.letterSalutationControl.value?.name
      ? this.setLetterSalutationText(this.letterSalutationControl.value)
      : this.letterSalutationControl.value;
    result.addressSalutation = result.addressSalutation
      ? this.translateSrv.instant(result.addressSalutation)
      : '';

    return result;
  }
}
